import React, { useEffect, useState } from "react";
import { Location, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { Icons } from "../../data/Assets";

import { openModal } from "../../store/slices/modal";

import useSignOut from "../../hooks/auth/useSignOut";

import { SVGIcon } from "../wrappers/SVGIcon";

interface INavItem {
    title: string;
    icon: string;
    link?: string;
    clickFn?: () => void;
};

interface ISidebarMenuGroup {
    title?: string;
    items: INavItem[];
    location: Location;
};
interface ISidebarMenuItem extends INavItem {
    isSelected: boolean;
    location: Location;
};

interface ISidebarSection {
    type: 'normal' | 'dropdown';
    title?: string;
    items: INavItem[];
};

const Sidebar = () => {
    const location = useLocation();
    const dispatch = useDispatch();

    const sections: ISidebarSection[] = [
        {
            type: 'normal',
            items: [
                { title: "Students", icon: Icons.UsersFill, link: "/students" },
                { title: "Resources", icon: Icons.News, link: "/resources" },
                { title: "Banners", icon: Icons.ImageUpload, link: "/banners" },
            ]
        },
        {
            type: 'dropdown',
            title: 'Reports',
            items: [
                { title: "User Sign Up", icon: Icons.UserCard, link: "/reports/user-sign-up" },
                { title: "Active Users", icon: Icons.UserCard, link: "/reports/active-user" },
                { title: "User Scores", icon: Icons.UserCard, link: "/reports/user-scores" },
                { title: "Interviews Completed", icon: Icons.UserCard, link: "/reports/interviews-completed" },
                { title: "Active User Demographics", icon: Icons.UserCard, link: "/reports/active-user-demographics" },
                { title: "User Jobs", icon: Icons.UserCard, link: "/reports/user-jobs" },
                { title: "User Visits", icon: Icons.UserCard, link: "/reports/user-visits" },
                { title: "Page Views", icon: Icons.UserCard, link: "/reports/page-views" },
                { title: "Certificate Awards", icon: Icons.UserCard, link: "/reports/certificate-awards" },
            ]
        },
        {
            type: 'normal',
            items: [
                { title: "Support", icon: Icons.QuestionChat, link: "/immigration-help" },
                { title: "Settings", icon: Icons.GearFill, link: "/settings" },
                { title: "Log Out", icon: Icons.LogOff, clickFn: () => openLogoutModal() },
            ]
        },
    ];

    const { mutate: signOut } = useSignOut();

    const openLogoutModal = () => {
        dispatch(openModal({
            name: 'MESSAGE_MODAL',
            data: {
                view: 'CONFIRMATION',
                closeBtn: false,
                title: "Confirm sign out?",
                content: "Do you want to sign out of your account?",
                actionTxt: "Sign out",
                action: () => signOut(),
            }
        }))
    };


    return (
        <div className='immersion__sidebar'>
            {sections.map((section, index) => (
                section.type === 'normal'
                    ? <SidebarMenuGroup
                        key={section.title ?? `section-${index}`}
                        title={section.title}
                        items={section.items}
                        location={location}
                    />
                    : <SiderbarDropdownSection
                        key={section.title ?? `section-${index}`}
                        title={section.title}
                        items={section.items}
                        location={location}
                    />
            ))}
        </div>
    );
};

export default Sidebar;

const SiderbarDropdownSection = (props: ISidebarMenuGroup) => {
    const { title, items, location } = props;

    const [isOpen, setIsOpen] = useState(true);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    // check if any of the items are active
    useEffect(() => {
        const isActive = items.some(item => item.link && location.pathname === item.link);
        if (isActive) setIsOpen(true);
    }, [location.pathname, items]);

    return (
        <div className="immersion__sidebar__dropdown_section">
            <button
                className={`immersion__sidebar__dropdown_section__header ${isOpen ? "immersion__sidebar__dropdown_section__header__open" : ""}`}
                onClick={toggleDropdown}
            >
                <span>{title}</span>
                <SVGIcon src={Icons.TriangleFill} />
            </button>
            {isOpen && (
                <div className="immersion__sidebar__dropdown_section__items">
                    <SidebarMenuGroup
                        items={items}
                        location={location}
                    />
                </div>
            )}
        </div>
    );
};

const SidebarMenuGroup = (props: ISidebarMenuGroup) => {
    const { title, items, location } = props;

    const getActiveLink = (link: string | undefined) => {
        if (!link) return false;

        if (link === "/foreign-workers") return location.pathname === link || location.pathname.includes('/foreign-workers/');
        if (link === "/schools") return location.pathname === link || location.pathname.includes('/schools/');

        return location.pathname === link;
    };

    return (
        <div className="immersion__sidebar__menu_group">
            {title && <h5 className="immersion__sidebar__menu_group__title">{title}</h5>}
            {items.map(item => (
                <SidebarMenuItem
                    key={item.title}
                    title={item.title}
                    icon={item.icon}
                    isSelected={getActiveLink(item.link)}
                    clickFn={item.clickFn}
                    link={item.link}
                    location={location}
                />
            ))}
        </div>
    );
};

const SidebarMenuItem = (props: ISidebarMenuItem) => {
    const { title, icon, clickFn, link, isSelected, location } = props;

    const navigate = useNavigate();

    const handleClick = () => {
        if (clickFn) clickFn();

        if (link && location.pathname !== link) navigate(link);
    };

    return (
        <button
            onClick={handleClick}
            className={`immersion__sidebar__menu__item ${isSelected ? "immersion__sidebar__menu__item__active" : ""}`}
        >
            <SVGIcon src={icon} />
            <span>{title}</span>
        </button>
    );
};
